import * as z from "zod";

const returnProductSchema = z.object({
  returnItemId: z.string().nullable(),
  title: z.string().nullable(),
  variantTitle: z.string().nullable(),
  sku: z.string().nullable(),
  image: z.string().nullable(),
  price: z.string().nullable(),
  quantity: z.number().nullable(),
  strategy: z.string().nullable(),
  status: z.string().nullable(),
  reason: z.string().nullable(),
  merchantNotes: z.string().nullable(),
  merchantGrade: z.string().nullable(),
  merchantOutcome: z.string().nullable(),
  merchantImages: z.array(z.string()).nullable(),
  damaged: z.boolean().nullable(),
  isFlagged: z.boolean().nullable(),
  exchangeFor: z
    .object({
      productTitle: z.string().nullable(),
      variantTitle: z.string().nullable(),
    })
    .nullable(),
  inputAnswers: z
    .array(z.object({ image: z.string().nullable(), inputType: z.string() }))
    .nullable(),
});

const shipmentSchema = z.object({
  trackingNumber: z.string().nullable(),
  carrier: z.string().nullable(),
  status: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  parcel: z
    .object({
      length: z.number().nullable(),
      width: z.number().nullable(),
      height: z.number().nullable(),
      weight: z.number().nullable(),
    })
    .nullable(),
});

const timelineEntrySchema = z.object({
  message: z.string().nullable(),
  subMessage: z.string().nullable(),
  isSystem: z.boolean(),
  imageUrls: z.array(z.string()).nullable(),
  createdAt: z.string().nullable(),
});

const returnInfoSchema = z.object({
  id: z.string(),
  teamId: z.string().nullable(),
  externalId: z.string().nullable(),
  status: z.string().nullable(),
  channel: z.string().nullable(),
  provider: z.string().nullable(),
  createdAt: z.string().nullable(),
  processedAt: z.string().nullable(),
  processedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  completedAt: z.string().nullable(),
  customer: z
    .object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      email: z.string().nullable(),
      phoneNumber: z.string().nullable(),
      shopifyCustomerId: z.string().nullable(),
    })
    .nullable(),
  shippingAddress: z.any().nullable(),
  products: z.array(returnProductSchema),
  cancelledProducts: z.array(returnProductSchema),
  shipments: z.array(shipmentSchema),
  timeline: z.array(timelineEntrySchema),
  tags: z
    .array(z.object({ name: z.string(), color: z.string().nullable() }))
    .nullable(),
  rejectReason: z.string().nullable(),
  exchangeOrderIds: z.array(z.string()).nullable(),
  draftOrderURL: z.string().nullable(),
  packingSlipLink: z.string().nullable(),
});

const processingEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  sessionItemId: z.string().nullable(),
  legacyUserId: z.string().nullable(),
  data: z.any().nullable(),
  createdAt: z.string(),
});

const processingSessionItemSchema = z.object({
  id: z.string(),
  returnItemId: z.string().nullable(),
  inventoryItemId: z.string().nullable(),
  status: z.string(),
  grade: z.string().nullable(),
  outcome: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const verificationItemPhotoSchema = z.object({
  url: z.string(),
  note: z.string().optional(),
});

const verificationItemSchema = z.object({
  id: z.string(),
  returnItemId: z.string().nullable(),
  kind: z.string(),
  result: z.string(),
  photos: z.array(verificationItemPhotoSchema).nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
});

const verificationSchema = z.object({
  id: z.string(),
  result: z.string(),
  performedAt: z.string(),
  partnerName: z.string().nullable(),
  partnerReference: z.string().nullable(),
  items: z.array(verificationItemSchema),
  createdAt: z.string(),
});

const processingSessionSchema = z.object({
  id: z.string(),
  type: z.string().nullable(),
  returnId: z.string(),
  legacyUserId: z.string().nullable(),
  locationId: z.string().nullable(),
  locationName: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  items: z.array(processingSessionItemSchema),
  events: z.array(processingEventSchema),
  verification: verificationSchema.nullable(),
});

const inventoryItemEventSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  payload: z.any().nullable(),
  createdAt: z.string(),
});

const inventoryLevelLedgerEntrySchema = z.object({
  id: z.string(),
  kind: z.string(),
  delta: z.number(),
  referenceId: z.string().nullable(),
  notes: z.string().nullable(),
  userName: z.string().nullable(),
  createdAt: z.string(),
});

const inventoryLevelSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  storageLocationId: z.string(),
  storageLocationName: z.string().nullable(),
  ledgerEntries: z.array(inventoryLevelLedgerEntrySchema),
});

const inventoryItemSchema = z.object({
  id: z.string(),
  kind: z.string(),
  grade: z.string().nullable(),
  disposition: z.string().nullable(),
  itemClass: z.string().nullable(),
  quantity: z.number(),
  productId: z.string().nullable(),
  lotNumber: z.string().nullable(),
  expirationDate: z.string().nullable(),
  locationId: z.string(),
  storageLocationId: z.string().nullable(),
  storageLocationName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  events: z.array(inventoryItemEventSchema),
  inventoryLevels: z.array(inventoryLevelSchema),
});

const trackingInfoSchema = z
  .object({
    number: z.string().nullable(),
    company: z.string().nullable(),
    url: z.string().nullable(),
  })
  .nullable();

const fulfillmentTrackingSchema = z.object({
  name: z.string(),
  status: z.string(),
  trackingInfo: trackingInfoSchema,
  totalQuantity: z.number(),
  deliveredAt: z.string().nullable(),
  estimatedDeliveryAt: z.string().nullable(),
  lineItems: z.array(z.object({ id: z.string(), quantity: z.number() })),
});

const fulfillmentGroupLineItemSchema = z.object({
  id: z.string(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  productTitle: z.string(),
  variantTitle: z.string().nullable(),
  quantity: z.number(),
  imageUrl: z.string().nullable(),
});

const fulfillmentGroupSchema = z.object({
  id: z.string(),
  status: z.string(),
  orderName: z.string().nullable(),
  customerName: z.string().nullable(),
  destinationAddress: z.any().nullable(),
  pickSessionId: z.string().nullable(),
  pickStatus: z.string().nullable(),
  pickedAt: z.string().nullable(),
  assignedPickerId: z.string().nullable(),
  batchId: z.string().nullable(),
  prettyBatchId: z.string().nullable(),
  noteFromBuyer: z.string().nullable(),
  noteToBuyer: z.string().nullable(),
  internalNote: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lineItems: z.array(fulfillmentGroupLineItemSchema),
  fulfillments: z.array(fulfillmentTrackingSchema),
});

const pickSessionItemSchema = z.object({
  fulfillmentGroupId: z.string(),
  orderName: z.string().nullable(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  productTitle: z.string(),
  variantTitle: z.string(),
  totalQuantity: z.number(),
  quantityPicked: z.number(),
  quantityOutOfStock: z.number(),
  bin: z.string().nullable(),
  secondaryBins: z.array(z.string()),
  storageLocationId: z.string().nullable(),
  inventoryItemId: z.string().nullable(),
});

const pickSessionSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  status: z.string(),
  strategy: z.string(),
  scanType: z.string(),
  locationId: z.string().nullable(),
  completedAt: z.string().nullable(),
  pickedAt: z.string().nullable(),
  printedAt: z.string().nullable(),
  createdAt: z.string(),
  items: z.array(pickSessionItemSchema),
});

const getReturnLifecycleOutput = z.object({
  resolvedAs: z.string(),
  returns: z.array(returnInfoSchema),
  processingSessions: z.array(processingSessionSchema),
  inventoryItems: z.array(inventoryItemSchema),
  fulfillmentGroups: z.array(fulfillmentGroupSchema),
  pickSessions: z.array(pickSessionSchema),
});

const getReturnLifecycleInput = z.object({
  returnIdentifier: z.string(),
  merchantTeamId: z.string().optional(),
});

export const getReturnLifecycle = {
  name: "getReturnLifecycle" as const,
  input: getReturnLifecycleInput,
  output: getReturnLifecycleOutput,
};
